import { CSSProperties, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  getRoyaltyMockSelection,
  ROYALTY_MOCK_OFF,
  ROYALTY_MOCK_STATE_OPTIONS,
  RoyaltyMockSelection,
  setRoyaltyMockSelection,
} from 'utils/dev-royalty-mocks.utils';

/**
 * A dev-only switch for the royalty → VIP Points mock, and NOTHING else.
 *
 * Built in the style of `DevDebugHeader` — same `IS_DEV` gate, same `sessionStorage`-backed
 * "choose, then reload" contract, same monospace dev-affordance look — but deliberately NOT part
 * of it. That panel owns staking's machinery: it dispatches `setWalletAddress`/`setIsConnected`,
 * impersonates wallets, and flips the network override. Putting this select inside it meant
 * shipping all of that to `/home/profile`, where a stray Apply writes the viewer's OWN address
 * into `dev:impersonatedWallet` — after which `useAccountChange` stops fetching their profile for
 * that address and the session reads as disconnected with no way to reconnect. A mock dropdown
 * has no business carrying that risk, so it lives here with no redux, no wallet actions and no
 * network override; the only two things it can do are write one `sessionStorage` key and reload.
 *
 * The whole layer it drives is documented in `utils/dev-royalty-mocks.utils`.
 */

/**
 * The pages that render the perk: the VIP page's estimate panel, and the profile card plus its
 * claim modal. A child path counts — the card lives on `/home/profile/collected` — so this is an
 * exact-or-child test. Nowhere else has anything to show, so nowhere else gets the control.
 */
const ALLOWED_PATHS = ['/vip', '/home/profile'];

const containerStyle: CSSProperties = {
  position: 'fixed',
  right: 12,
  bottom: 12,
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 10px',
  background: '#1a1f3d',
  border: '1px solid #F83A56',
  borderRadius: 6,
  color: '#fff',
  fontFamily: 'monospace',
  fontSize: 12,
};

const handleStyle: CSSProperties = {
  ...containerStyle,
  gap: 6,
  padding: '4px 10px',
  fontSize: 10,
  letterSpacing: 1,
  cursor: 'pointer',
  userSelect: 'none',
};

const activeDotStyle: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: '#F83A56',
};

const selectStyle: CSSProperties = {
  background: '#0e1226',
  border: '1px solid #2D5EFF',
  borderRadius: 4,
  color: '#fff',
  padding: '4px 8px',
  fontFamily: 'inherit',
  fontSize: 12,
  maxWidth: 280,
};

const closeButtonStyle: CSSProperties = {
  background: 'transparent',
  border: '1px solid #435186',
  borderRadius: 4,
  color: '#9bb0ff',
  padding: '2px 8px',
  fontSize: 14,
  lineHeight: 1,
  fontWeight: 700,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

export const DevRoyaltyMockSwitch = () => {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);

  /**
   * Read at render rather than captured in a module constant, purely so a test can set `NODE_ENV`
   * before mounting. Same expression and same meaning as `DevDebugHeader`'s `IS_DEV`; CRA's
   * DefinePlugin still substitutes it literally, so a production build compiles the guard to
   * `false` and drops everything below it.
   */
  const isDev = process.env.NODE_ENV === 'development';

  const normalizedPath = location.pathname.replace(/\/+$/, '');
  const isOnAllowedPath = ALLOWED_PATHS.some(
    (allowed) => normalizedPath === allowed || normalizedPath.startsWith(`${allowed}/`)
  );

  if (!isDev) return null;
  if (!isOnAllowedPath) return null;

  // The committed selection. Not state: it only ever changes via the reload below, so re-reading
  // it on render is both correct and the whole story.
  const selection = getRoyaltyMockSelection();
  const isActive = selection !== ROYALTY_MOCK_OFF;

  /**
   * Commit and reload. A reload rather than a query invalidation, for the same reason the staking
   * bar reloads: the preview sits under a React Query key with a 300s `staleTime` and no refetch
   * on focus, and the claimable balance is read by `useSelectedWalletRoyalties`, whose cooldown
   * lives in a `useRef` no cache invalidation can reach. Refreshing only what a cache can see
   * would leave one mock state on screen beside the leftovers of another.
   */
  const commit = (next: RoyaltyMockSelection) => {
    if (next === selection) return;
    setRoyaltyMockSelection(next);
    window.location.reload();
  };

  if (!isExpanded) {
    return (
      <div
        style={handleStyle}
        onClick={() => setIsExpanded(true)}
        data-testid="dev-royalty-mock-handle"
        role="button"
        aria-label="Open the fake royalties switch"
        title="Open the fake royalties switch"
      >
        {isActive && <span style={activeDotStyle} />}
        <span>{isActive ? `▴ FAKE ROYALTIES · ${selection}` : '▴ FAKE ROYALTIES'}</span>
      </div>
    );
  }

  return (
    <div style={containerStyle} data-testid="dev-royalty-mock-switch">
      {/*
        "FAKE" rather than "mock": the figures this produces look exactly like real money on
        screen, and the one mistake worth designing against is someone reading a screenshot of it
        as production data.
      */}
      <span style={{ color: '#9bb0ff' }}>FAKE royalties</span>
      <select
        value={selection}
        onChange={(event) => commit(event.target.value as RoyaltyMockSelection)}
        style={{ ...selectStyle, borderColor: isActive ? '#F83A56' : '#2D5EFF' }}
        data-testid="dev-royalty-mock-select"
        aria-label="Fake royalty data state"
        title="Serve canned royalty → VIP Points data instead of calling the two providers. Reloads on change."
      >
        <option value={ROYALTY_MOCK_OFF}>off — use the real providers</option>
        {ROYALTY_MOCK_STATE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => setIsExpanded(false)}
        style={closeButtonStyle}
        title="Collapse (does not change the selection)"
        aria-label="Collapse the fake royalties switch"
      >
        ×
      </button>
    </div>
  );
};

export default DevRoyaltyMockSwitch;
